import type { AnalysisResult, TimelinePoint, TimelineSegment, TimelineVisit } from '../domain/models';

type GeoJsonFeature = {
  type: 'Feature';
  geometry: Record<string, unknown>;
  properties: Record<string, unknown>;
};

export function analysisToGeoJson(result: AnalysisResult): string {
  const features: GeoJsonFeature[] = [
    ...result.segments.map(segmentFeature),
    ...result.visits.map(visitFeature),
    ...samplePoints(result.points).map(pointFeature),
  ];

  return JSON.stringify(
    {
      type: 'FeatureCollection',
      features,
    },
    null,
    2,
  );
}

function segmentFeature(segment: TimelineSegment): GeoJsonFeature {
  return {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: segment.path.map((point) => [point.lng, point.lat]),
    },
    properties: {
      id: segment.id,
      kind: 'segment',
      startTime: segment.startTime,
      endTime: segment.endTime,
      distanceMeters: Math.round(segment.distanceMeters),
      mode: segment.inferredMode ?? 'unknown',
      confidence: segment.confidence,
    },
  };
}

function visitFeature(visit: TimelineVisit): GeoJsonFeature {
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [visit.center.lng, visit.center.lat],
    },
    properties: {
      id: visit.id,
      kind: 'visit',
      startTime: visit.startTime,
      endTime: visit.endTime,
      durationMinutes: Math.round(visit.durationMinutes),
      placeName: visit.placeName ?? '',
      confidence: visit.confidence,
    },
  };
}

function pointFeature(point: TimelinePoint): GeoJsonFeature {
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [point.lng, point.lat],
    },
    properties: {
      id: point.id,
      kind: 'point',
      timestamp: point.timestamp,
      source: point.source,
    },
  };
}

function samplePoints(points: TimelinePoint[], maxPoints = 5000): TimelinePoint[] {
  if (points.length <= maxPoints) return points;
  const step = Math.ceil(points.length / maxPoints);
  return points.filter((_, index) => index % step === 0);
}
